import last from 'lodash/last';
import pick from 'lodash/pick';
import isString from 'lodash/isString';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment-timezone';
import currencyFormatter from 'currency-formatter';
import { capitalizeFirstLetter } from './util';
import * as searchActions from 'constants/searchActions';
import * as conversationTypes from 'constants/conversationTypes';
import * as sensitiveDataTypes from 'constants/sensitiveDataTypes';
import { NEW, READ, UPDATED, BLOCK_DAILY, BLOCK_WEEKLY } from 'constants/alerts';

export const getChannelTypeFromSearchType = (searchType) => {
  switch (searchType) {
    case searchActions.COMMON_ORDER_EMAIL:
    case searchActions.SIMPLR_ORDER_SUBSCRIPTION_EMAIL:
    case searchActions.COMMON_SUBSCRIPTION_EMAIL:
    case searchActions.SHOPIFY_EMAIL:
    case searchActions.RBI_EMAIL:
    case searchActions.SIMPLR_ORDER_EMAIL:
    case searchActions.SALESFORCE_ORDER_EMAIL:
    case searchActions.BIG_COMMERCE_ORDER_EMAIL:
    case searchActions.REVOLVE_ORDER_EMAIL:
      return conversationTypes.EMAIL;
    // order number does not need to be masked
    case searchActions.SHOPIFY_ORDER_NUMBER:
      return null;
    default:
      return null;
  }
};

export const formatFromSearchType = (searchType, fromChannel) => {
  const channelType = getChannelTypeFromSearchType(searchType);
  if (channelType) {
    return formatFromChannel(channelType, fromChannel);
  }

  return fromChannel;
};

export const maskEmailAddress = (emailAddress) => {
  if (!emailAddress) {
    return null;
  }
  const emailParts = emailAddress.split('@');
  if (emailParts.length < 2 || emailParts.includes('')) {
    return null;
  }

  const domainParts = emailParts[1].split('.');
  if (domainParts) {
    return `${emailParts[0].slice(0, 4)}***@***.${domainParts[domainParts.length - 1]}`;
  }

  return null;
};

export const formatFromChannel = (channelType, fromChannel) => {
  if (!fromChannel) {
    return '';
  }

  if (channelType === 'SMS') {
    return `XXX-XXX-${fromChannel.slice(-4)}`;
  }
  if (channelType === 'EMAIL') {
    const maskedEmailAddress = maskEmailAddress(fromChannel);
    if (maskedEmailAddress) {
      return maskedEmailAddress;
    }
  }

  return '';
};

export const getSalesforceTicketDisplayName = (customerFirstName, isAdmin, platformTicketId) => {
  const name = customerFirstName || 'Customer';

  return isAdmin ? `${name} (${platformTicketId})` : name;
};

export const truncateWithEllipsis = (str, truncateLength) => {
  if (str && typeof truncateLength === 'number') {
    let truncatedStr = str.slice(0, truncateLength);
    if (str.length > truncateLength) {
      truncatedStr = truncatedStr + '...';
    }

    return truncatedStr;
  }

  throw new TypeError('truncateWithEllipsis arguments are invalid');
};

export const formatAssociatedOrderNumberForDisplay = (value) => {
  if (!value) {
    return null;
  }

  return value;
};

export const formatDateTime = (dateObj) => {
  if (!dateObj) {
    return null;
  }

  return moment(dateObj).format('MM/DD/YY h:mm a');
};

export const formatDateWithoutTime = (dateObj) => {
  if (!dateObj) {
    return 'N/A';
  }

  return moment(dateObj).format('MM/DD/YY');
};

// formats simplrTicketNumber (smbName_123) to number only (123)
export const formatTicketNumber = (ticketNumber) => {
  if (ticketNumber) {
    return ticketNumber.match(/[^_]*$/)[0] || 'NA';
  }

  return 'NA';
};

// formatOrderDateToLocalTime - formats to user's local time
export const formatOrderDateToLocalTime = (date, orderingSystem) => {
  if (!date) {
    return '-';
  }

  const ISOorderingSystems = ['custom_rbi', 'shopify', 'amazon', 'salesforce', 'revolve'];
  const UnixOrderingSystems = ['simplrOrder'];

  if (ISOorderingSystems.includes(orderingSystem) && !moment(date, [moment.ISO_8601], true).isValid()) {
    return '-';
  }

  if (UnixOrderingSystems.includes(orderingSystem) && !moment.unix(date).isValid()) {
    return '-';
  }

  const timezone = new Date().toLocaleTimeString('en-us', { timeZoneName: 'short' }).split(' ')[2];
  const dateFormat = `M/D/YY [at] h:mm a [${timezone}]`;
  switch (orderingSystem) {
    case 'simplrOrder':
      return moment.unix(date).format(dateFormat);
    case 'custom_rbi':
    case 'bigCommerce': {
      return moment.utc(date).local().format(dateFormat);
    }
    case 'salesforce':
      return moment.utc(date).format('MM/DD/YY');
    case 'shopify':
    case 'amazon':
    case 'revolve':
    case 'common':
    default: {
      return moment(date).format(dateFormat);
    }
  }
};

// for formatting zendesk ticket fields to provide category/subcategory levels
// of dropdowns for tagging currently making the assumption that there are no
// more than 3 subcategories based on prod data - TODO - generalize later input
// array structure: [   {     id: 360007149654,     name: 'Pre-Sales::Product
// Question',     raw_name: 'Pre-Sales::Product Question',     value:
// 'pre-sales__product_question',     default: false,   },  ... ] output tree
// structure:
/*
{
  'pre-sales': {
    value: 'pre-sales',
    name: 'Pre-Sales',
    children: {
      policy_question: {
        value: 'pre-sales__policy_question',
        name: 'Policy Question',
        children: {
          medium: {
            value: 'pre-sales__policy_question__medium',
            name: 'Medium',
            children: null
          },
          easy: {
            value: 'pre-sales__policy_question__easy',
            name: 'Easy',
            children: null
          },
          hard: {
            value: 'pre-sales__policy_question__hard',
            name: 'Hard',
            children: null
          }
        }
      },
      product_question: {
        value: 'pre-sales__product_question',
        name: 'Product Question',
        children: null
      }
    }
  }
}
*/

/**
 * Currency human formatter
 * @see https://www.npmjs.com/package/currency-formatter
 * @param {string|number} price
 * @param {string} [code="USD"]
 * @param {object} [options]
 * @param {string} [options.code] - 'USD'
 * @param {string} [options.symbol] - '$'
 * @param {string} [options.thousandsSeparator] - ','
 * @param {string} [options.decimalSeparator] - '.'
 * @param {boolean} [options.symbolOnLeft] - true
 * @param {boolean} [options.spaceBetweenAmountAndSymbol] - false
 * @param {number} [options.decimalDigits] - 2
 * @return {string}
 */
export const priceFormat = (price, code = 'USD', options = {}) => {
  if (code === 'CAD') {
    options = { symbol: 'C$', ...options };
  }

  return currencyFormatter.format(price, {
    code,
    ...options,
  });
};

/**
 * Check  is today date
 * @param {string|number|Date|Moment} timePoint
 * @return {boolean}
 */
export function isToday(timePoint) {
  const momentDate = moment(timePoint);
  const today = moment().startOf('day');

  return momentDate.isSame(today, 'd');
}

/**
 * Check is yesterday date
 * @param {string|number|Date|Moment} timePoint
 * @return {boolean}
 */
export function isYesterday(timePoint) {
  const momentDate = moment(timePoint);
  const yesterday = moment().subtract(1, 'days').startOf('day');

  return momentDate.isSame(yesterday, 'd');
}

/**
 * Decorate date with Today, Yesterday, or common date format
 * @param {Date} date
 * @param {string} format - Moment format
 * @param {oolean} boolean
 * @return {string}
 */
export function dateDecorator(date, format = 'MM/DD/YY [at] h:mma', lowercase = false) {
  if (isToday(date)) {
    return lowercase ? `${moment(date).format('h:mm A')} today` : `${moment(date).format('h:mm A')} Today`;
  }
  if (isYesterday(date)) {
    return lowercase ? `${moment(date).format('h:mm A')} yesterday` : `${moment(date).format('h:mm A')} Yesterday`;
  }

  return moment(date).format(format);
}

/**
 * Parses date string as UTC using moment.js
 * @param {string} date
 * @return {Date}
 */
export const parseUTCDate = (date) => {
  return date ? moment.utc(date).local() : null;
};

export const formatDate = (date) => {
  return date && moment(date).format('MM/DD/YYYY');
};

export const capitalizeKeysInObject = (obj) => {
  const result = {};
  Object.keys(obj).forEach((key) => {
    const capitalizedKey = key.toUpperCase();
    const value = obj[key];
    result[capitalizedKey] = value;
  });

  return result;
};

export const addEmptyArraysAsValuesToObject = (categories = []) => {
  const result = {};
  categories.forEach((tag) => {
    result[tag] = [];
  });

  return result;
};

export const organizeMacroData = (categories = [], macroList = []) => {
  const macroListByPath = {};
  categories.forEach((categoryTag) => {
    macroList.forEach((macro) => {
      // if a macro has no tag, it won't show up in the macro wizard
      let tags = macro.tags || [];
      // filter out null tags
      tags = tags.filter((tag) => !!tag);

      // normalize the tags if there are extra spaces
      tags = tags.map((tag) => {
        const splitTag = splitTagPath(tag);

        return joinTagArray(splitTag);
      });

      if (tags.includes(categoryTag)) {
        macroListByPath[categoryTag] = macroListByPath[categoryTag] || [];
        macroListByPath[categoryTag].push(macro);
      }
    });
  });

  return macroListByPath;
};

export const splitTagPath = (path = '') => {
  return path
    .split('--')
    .map((item) => item.trim())
    .filter((item) => !!item);
};

export const joinTagArray = (tagAray) => {
  return tagAray.join(' -- ');
};

export const formatTextForDecisionTreeButton = (text) => {
  return text.split('_').map(capitalizeFirstLetter).join(' ');
};

export const removeLastTagFromPath = (path) => {
  const splitPath = splitTagPath(path);
  if (splitPath.length < 1) {
    throw new Error('Expected tag path of at least length 1');
  }
  splitPath.pop();

  return joinTagArray(splitPath);
};

export const formatListWithCommas = (textList) => {
  switch (textList.length) {
    case 0:
      return '';
    case 1:
      return textList[0];
    case 2:
      return textList[0] + ' and ' + textList[1];
    default:
      let text = textList.slice(0, -1).join(', ');

      return text + ', and ' + last(textList);
  }
};

export const checkIfBusinessAlertIsRead = (businessAlerts, businessAlertReadData) => {
  return businessAlerts.map((alert) => {
    const businessAlertRead = businessAlertReadData.find((data) => {
      return data.businessAlertPtr.objectId === alert.id;
    });
    if (businessAlertRead) {
      const lastReadAt = businessAlertRead.lastReadAt;

      if (moment(lastReadAt).isAfter(alert.updatedAt)) {
        alert.readStatus = READ;
      } else {
        alert.readStatus = UPDATED;
      }

      const lastReadDays = moment().diff(moment(lastReadAt), 'days');

      if (alert.blockType === BLOCK_DAILY) {
        if (lastReadDays >= 1) {
          alert.readStatus = NEW;
        }
      }

      if (alert.blockType === BLOCK_WEEKLY) {
        if (lastReadDays >= 7) {
          alert.readStatus = NEW;
        }
      }

      return alert;
    }
    alert.readStatus = NEW;

    return alert;
  });
};

export const formatTrackingNumber = (trackingNumber) => {
  const trimmed = trackingNumber.trim();

  return trimmed.replace(/\s/g, '');
};

export const getDiscountPeriodFields = (periods) => {
  return periods.map((period) => {
    const jsPeriod = pick(period, [
      'title',
      'discount',
      'discountType',
      'objectId',
      'couponCode',
      'discountQualificationInstructions',
      'discountDescription',
    ]);
    // pull the date values with get since they are dates
    jsPeriod.discountPeriodStartDate = period.discountPeriodStartDate;
    jsPeriod.discountPeriodEndDate = period.discountPeriodEndDate;
    jsPeriod.gracePeriodStartDate = period.gracePeriodStartDate;
    jsPeriod.gracePeriodEndDate = period.gracePeriodEndDate;
    jsPeriod.expirationDate = period.expirationDate;

    return jsPeriod;
  });
};

export const splitAtCamelCase = (value) => {
  if (!value || typeof value !== 'string') {
    return '';
  }

  return value
    .split(/(?=[A-Z])/)
    .join(' ')
    .toLowerCase();
};

const isVowel = (letter) => {
  if (!letter) {
    return false;
  }

  return ['a', 'e', 'i', 'o', 'u'].includes(letter.toLowerCase());
};

// Splits by camel case and prefixes with "Enter a"
export const formatPlaceholder = (value) => {
  if (!value || typeof value !== 'string') {
    return value;
  }

  const connector = value && isVowel(value[0]) ? 'an' : 'a';

  return 'Enter ' + connector + ' ' + splitAtCamelCase(value);
};

export const splitAndCapitalizeCamelCase = (value) => {
  return truncateWithEllipsis(
    value.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase()),
    19
  );
};

export const splitAtUnderscoreOrCamelCaseAndUpperCase = (value) => {
  return value
    .replace(/([A-Z])/g, ' $1')
    .split('_')
    .join(' ')
    .toUpperCase();
};

export const splitAtUnderscoreCamelCaseOrDotAndUpperCase = (value) => {
  return value
    .replace(/([A-Z])/g, ' $1')
    .split('_')
    .join(' ')
    .split('.')
    .join(' ')
    .toUpperCase();
};

export const obfuscateSensitiveData = (data, dataType) => {
  if (!data) {
    return '';
  }

  switch (dataType) {
    case sensitiveDataTypes.email:
      return maskEmailAddress(data);
    case sensitiveDataTypes.lastName:
      return data.slice(0, 1) + '****';
    case sensitiveDataTypes.fullName:
      const nameArray = data.split(' ');

      return `${nameArray[0]} ****`;
    case sensitiveDataTypes.phoneNumber:
      return '***-***-' + data.split('').reverse().join('').slice(0, 4);
    default:
      return '****';
  }
};

export const truncateThousands = (number) => {
  return number > 999 ? (number / 1000).toFixed(1) : number;
};

export const formatCentsToDollars = (number) => {
  return (number / 100).toFixed(2);
};

export function makeDisplayName({ id, firstName = '', lastName = '' } = {}) {
  if (firstName || lastName) {
    const fullName = `${firstName} ${lastName}`.trim();

    if (!id) {
      return fullName;
    }

    return `${fullName} (${id})`;
  }
}

export const addPrefixToEsclationMessage = (message) => {
  if (!message || typeof message !== 'string') {
    return message;
  }

  const prefix = 'Escalation Reason: ';

  if (message.includes(prefix)) {
    return message;
  }

  return prefix + message;
};

export const maskCardNumber = (number) => {
  if (isString(number) && number.length > 4) {
    return '****' + number.slice(number.length - 4);
  }

  return null;
};

/**
 * Converts array to readable text with commas and an 'and'
 * @param {array} list
 * @return {string}
 */
export const makeReadableList = (list) => {
  if (isEmpty(list)) {
    return '';
  }

  if (list.length === 1) {
    return list[0];
  }

  return list.reduce((final, cur, i) => {
    return final + (i < list.length - 1 ? ', ' : ' and ') + cur;
  });
};

export const getPositiveIntOrNullFromString = (str) => {
  const parsed = parseInt(str);
  if (isNaN(parsed) || parsed <= 0) {
    return null;
  }

  return parseInt(str);
};

export const trimObjectValues = (obj) => {
  if (isEmpty(obj)) {
    return obj;
  }

  return Object.keys(obj).reduce((acc, key) => {
    acc[key] = obj[key];

    return acc;
  }, {});
};

/**
 * In-memory display names object, English localized.
 * Used by getLanguageDisplayName().
 */
export const LANGUAGE_NAMES_IN_ENGLISH = new Intl.DisplayNames(['en'], { type: 'language' });

/**
 * Get the language display name for a given language code.
 *
 * @param {string} languageCode ["-" scriptCode] ["-" regionCode ]
 *  languageCode is either a two-letter ISO 639-1 or a three-letter ISO 639-2 language code.
 * @return {(string|Error)}
 *
 * @example getLanguageDisplayName('uk') // < 'Ukrainian'
 * @example getLanguageDisplayName('en-US') // < 'American English'
 */
export const getLanguageDisplayName = (languageCode) => {
  if (languageCode === null) {
    return 'null';
  }

  if (languageCode === '') {
    return 'root';
  }

  try {
    return LANGUAGE_NAMES_IN_ENGLISH.of(languageCode);
  } catch (error) {
    const isValidLanguageCodeFormat =
      typeof languageCode === 'string' && languageCode.trim() === languageCode && !/^\d+$/.test(languageCode);

    if (isValidLanguageCodeFormat) {
      return languageCode.toLowerCase();
    }

    throw error;
  }
};
